'use client';

import * as Form from '@radix-ui/react-form';
import * as SelectPrimitive from '@radix-ui/react-select';
import {
  getPhoneNumberCountries,
  getPhoneNumberCountry,
  getDefaultPhoneNumberCountry,
  guessPhoneNumberCountryFromPhoneNumber,
} from '@tuic/lib/src/data';
import { useState } from 'react';
import { forwardRef } from 'react';
import CountryFlag from 'react-country-flag';
import { twMerge } from 'tailwind-merge';

import { Icon } from '../../../core-components';

import type { PhoneNumberCountry } from '@tuic/lib/src/data';
import type { ChangeEvent, InputHTMLAttributes } from 'react';
import type { ForwardRefRenderFunction } from 'react';
// import { CountryCode, parsePhoneNumber } from 'libphonenumber-js';

// Props for the PhoneNumberInput component
type PhoneNumberInputProps = InputHTMLAttributes<HTMLInputElement> & {
  countries?: PhoneNumberCountry[];
  defaultCountryCode?: string;
  invalid?: boolean;
  onValueChange?: (value: {
    dialCode: string;
    localNumber: string;
    fullNumber: string;
  }) => void;
};

/**
 * Returns the default country based on given defaultValue or defaultCountryCode.
 */
const getDefaultCountry = (
  defaultCountryCode: string,
  defaultValue: PhoneNumberInputProps['defaultValue'],
) => {
  const valueAsString = defaultValue?.toString() ?? '';

  if (valueAsString !== '') {
    const countryFromPhoneNumber =
      guessPhoneNumberCountryFromPhoneNumber(valueAsString);

    if (countryFromPhoneNumber) {
      return countryFromPhoneNumber;
    }
  }

  return (
    getPhoneNumberCountry(defaultCountryCode) ?? getDefaultPhoneNumberCountry()
  );
};
//
// /**
//  * Formats given user typed number to a E.164 number dialed from given country.
//  */
// const formatNumber = (
//   country: PhoneNumberCountry['code'],
//   typedNumber: string,
// ) => {
//   if (typedNumber === '') {
//     return '';
//   }
//
//   try {
//     const phoneNumber = parsePhoneNumber(typedNumber, country as CountryCode);
//
//     if (phoneNumber.number) {
//       return phoneNumber.number;
//     }
//   } catch (e) {}
//
//   const phoneNumberCountry = getPhoneNumberCountry(country);
//
//   if (!phoneNumberCountry) {
//     return typedNumber;
//   }
//
//   return `${phoneNumberCountry.dialCode} ${typedNumber}`;
// };

// PhoneNumberInput render function
const PhoneNumberInputRenderFunction: ForwardRefRenderFunction<
  HTMLDivElement,
  PhoneNumberInputProps
> = (props, ref) => {
  const {
    countries = getPhoneNumberCountries(),
    defaultCountryCode = 'DE',
    invalid = false,
    onValueChange,
    disabled,
    maxLength,
    ...attrs
  } = props;

  const seedValue = props.value ?? props.defaultValue;
  const defaultCountry = getDefaultCountry(defaultCountryCode, seedValue);

  const [typedInNumber, setTypedInNumber] = useState(
    seedValue?.toString().substring(defaultCountry.dialCode.length).trim() ||
      '',
  );

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    defaultCountry.code,
  );

  const selectedCountryDialCode =
    countries.find(country => country.code === selectedCountryCode)?.dialCode ||
    '';

  const handleCountryChange = (value: string) => {
    setSelectedCountryCode(value);
    setTypedInNumber('');
    onValueChange?.({
      dialCode:
        countries.find(country => country.code === value)?.dialCode ?? '',
      localNumber: '',
      fullNumber: '',
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fullNumber = event.target.value;
    const justNumber = fullNumber.replace(selectedCountryDialCode, '').trim();
    setTypedInNumber(justNumber);
    onValueChange?.({
      dialCode: selectedCountryDialCode,
      localNumber: justNumber,
      fullNumber: justNumber
        ? `${selectedCountryDialCode} ${justNumber}`.trim()
        : '',
    });
  };

  const inputValue = `${selectedCountryDialCode} ${typedInNumber}`;

  return (
    <div
      ref={ref}
      data-invalid={invalid ? 'true' : undefined}
      className={twMerge(
        'flex w-full items-center overflow-hidden rounded-md border border-stroke-secondary-40 bg-surface-white p-1 text-marine-high-emphasis focus-within:shadow-focus-state data-[invalid]:border-stroke-error-100',
        // Match the standard InputField disabled style.
        disabled &&
          'border-transparent bg-surface-secondary-7 text-marine-medium-emphasis',
      )}
    >
      <SelectPrimitive.Root
        value={selectedCountryCode}
        onValueChange={handleCountryChange}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger className="z-[1] flex items-center gap-1 rounded-md bg-surface-secondary-10 p-3 focus:outline-none focus-visible:shadow-focus-state disabled:bg-transparent">
          <CountryFlag
            svg
            countryCode={selectedCountryCode}
            className="!h-auto rounded-sm text-[1.25rem]"
          />
          {/* Radix' Trigger requires Value as a child to wire up its
              open/close mechanics. The outer span hides it visually while
              keeping the dial code accessible for screen readers — the flag
              identifies the country in the closed trigger. */}
          <span className="sr-only">
            <SelectPrimitive.Value />
          </span>
          <Icon
            name="nav-arrow-down"
            size="md"
            className="text-marine-high-emphasis"
          />
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content className="z-[9999] overflow-hidden rounded-md border border-stroke-secondary-40 bg-surface-white text-marine-high-emphasis drop-shadow-md">
            <SelectPrimitive.Viewport className="p-1">
              {countries.map(country => (
                <SelectPrimitive.Item
                  key={country.code}
                  value={country.code}
                  className="flex cursor-pointer items-center gap-1 p-3 focus:outline-none focus-visible:bg-surface-secondary-10 focus-visible:shadow-focus-state"
                >
                  <CountryFlag
                    svg
                    countryCode={country.code}
                    className="hidden !h-auto rounded-sm text-[1.25rem]"
                  />
                  <SelectPrimitive.ItemText>
                    {country.dialCode}
                  </SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      <input
        type="tel"
        className="flex-1 bg-transparent p-3 focus:outline-none disabled:text-marine-medium-emphasis"
        value={inputValue}
        onChange={handleChange}
        disabled={disabled}
        maxLength={maxLength}
      />
      <Form.Control type="tel" asChild>
        <input
          type="hidden"
          {...attrs}
          disabled={disabled}
          defaultValue={inputValue}
        />
      </Form.Control>
    </div>
  );
};

/**
 * `PhoneNumberInput` is a component that renders a phone number input with a country code select dropdown.
 *
 * ## Props
 *
 * The `PhoneNumberInput` component accepts the following properties:
 *
 * - `customCountries`: Optional custom array of country objects to override the default list. Each object should contain `code`, `dialCode` and `label`.
 * - `onCountryChange`: Optional callback function that is called when a new country is selected. It receives the dial code of the selected country as an argument.
 *
 * ## Usage
 *
 * ```tsx
 * <PhoneNumberInput defaultCountryCode="DE" />
 * ```
 */
export const PhoneNumberInput = forwardRef<
  HTMLDivElement,
  PhoneNumberInputProps
>(PhoneNumberInputRenderFunction);
